/**
 * SYM-5876. Which template previews survive a change to the ad's values.
 *
 * The picker shows six server-rendered pictures of the customer's own ad. Every one of them is
 * rendered WITH the current values -- hook line, salary, benefits, accent colour -- so the moment
 * any of those changes, all six are out of date.
 *
 * Only the selected one was being re-rendered, so the other five kept showing the ad as it was when
 * the panel opened. Steven typed a hook line, watched the template that renders it not change, and
 * reasonably concluded the edit had not saved.
 *
 * Lives here rather than inline in the context because the app has no component tests: a decision
 * kept inside a hook cannot be exercised at all, and this one is easy to get subtly wrong.
 */

/** A preview slot: rendering, rendered, or failed. Mirrors the context's own shape. */
export type PreviewSlot = {
  loading?: boolean;
  url?: string;
  error?: string;
  /**
   * SYM-6247. The server asked the editor to wait longer than a tile should spin (the per-user
   * limit is per minute), and a retry is arranged for when the wait is over. Shown as a calm
   * notice, neither a spinner nor an error. Always set together with `loading`, so everything that
   * treats a loading slot as "on its way" keeps doing so.
   */
  waiting?: boolean;
  /**
   * SYM-6214. The picture was asked for at full size, so it may be shown large on the canvas.
   * Absent means tile-sized (256px tall), which is fine in the panel and blurry on the canvas.
   * On a slot still loading it says what the request in flight asked for.
   */
  full?: boolean;
};

export type PreviewSlots = Record<string, PreviewSlot>;

/**
 * SYM-6214. Whether this slot already answers the CANVAS, so nothing more needs asking for.
 *
 * A tile-sized picture does not: panel tiles are now drawn small (measured on QA, 16 Sep 2026:
 * ~0.25s against ~0.7s full size), and shown at 400x500 a small one is visibly blurred. A slot
 * failed with a reason counts as answered, so this never asks twice for the same thing.
 *
 * ⚠️ A slot still LOADING answers only if what is on its way is full-size (Astra's third review,
 * point 2.4). "Loading" used to count whatever was asked for, so: panel open on design B, the
 * tile for A still queued, click A -- the canvas found A's slot loading, asked nothing, and the
 * tile then landed as the picture on the canvas, blurry, with nothing left to ask again.
 */
export const slotServesCanvas = (slot: PreviewSlot | undefined): boolean =>
  !!slot && (!!slot.error || !!slot.full);

/**
 * The preview map to keep after a value changed.
 *
 * Every slot is dropped EXCEPT the selected template's, which is kept because the caller
 * re-requests it directly and because it is the picture driving the main canvas -- clearing it
 * would blank what the customer is looking at on every keystroke.
 *
 * Dropping rather than marking stale is deliberate: the effect that fills these slots is driven by
 * which ones are MISSING, so removing them IS the request to re-render, and the tiles honestly say
 * "Rendering..." instead of showing an ad that no longer matches the form.
 */
export const previewsAfterValueChange = (
  previews: PreviewSlots,
  selectedCode: string | null | undefined
): PreviewSlots => {
  if (!selectedCode) {
    return {};
  }

  const kept = previews[selectedCode];

  // A selected template with no slot yet must not gain an empty one: an empty object is still a
  // slot, and the seeding effect skips codes that have one, so the tile would sit on "Rendering..."
  // forever waiting for a request nobody makes.
  return kept ? { [selectedCode]: kept } : {};
};

/**
 * SYM-5880. The picture the variant RAIL should show for a variant, or undefined to fall back to
 * the stored canvas artefacts.
 *
 * The rail draws `data_url` (a fabric canvas capture), then `svg` (canvas.toSVG() or the last
 * stencil the server generated), then the bare photo. A v2 template is drawn on the SERVER and laid
 * over the canvas, so none of those has ever seen it: with a template selected the rail showed the
 * PREVIOUS state while the canvas beside it showed the new one.
 *
 * TWO SOURCES, and which one answers depends on whether this is the ad being edited.
 *
 * The ad being edited is answered from `previews`, keyed by template code, because that map is
 * live: it tracks unsaved edits, so the rail moves with the canvas as the customer types.
 *
 * SYM-5975: every OTHER ad used to be answered with `undefined`, on the reasoning that its stored
 * artefact "is the right picture for it anyway, being its last saved state". That was true before
 * v2 templates existed. It is false now and false in the direction that matters, because the
 * stored artefact is a capture of the canvas UNDERNEATH the template. Steven reloaded a campaign
 * and found none of the thumbnails showed a badge or a coloured panel; clicking one made it appear.
 * So the rail was misrepresenting every ad the customer was not standing on.
 *
 * They are now answered from `railPreviews`, one server render per ad, keyed by railPreviewKey().
 * That map is NOT live and does not need to be: an ad you are not editing cannot have unsaved
 * changes.
 */
export const railPreviewUrl = (
  variant: RailPreviewVariant & { uuid?: string | null },
  currentVariantUuid: string | null | undefined,
  previews: PreviewSlots,
  railPreviews: Record<string, string> = {}
): string | undefined => {
  if (!variant.template || !variant.uuid) return undefined;

  const key = railPreviewKey(variant);
  const rail = key ? railPreviews[key] : undefined;

  if (variant.uuid !== currentVariantUuid)
    return rail ?? storedPreviewUrl(variant);

  /*
   * A slot that is still loading or has failed carries no url. Falling back to this ad's RAIL
   * render beats blanking the thumbnail mid-request, and beats the stored artefact, which is the
   * picture without the template on it.
   *
   * SYM-6214: the stored pictures come last for the ad being EDITED, and that order is the point.
   * `previews` tracks unsaved edits; `preview_url` was drawn at the last save. Preferring the
   * stored one here would freeze the thumbnail while the customer types.
   */
  return previews[variant.template]?.url ?? rail ?? storedPreviewUrl(variant);
};

/**
 * SYM-6214. The picture the server already holds for this ad, or undefined.
 *
 * Order is Steven's rule of 16 Sep 2026, not a preference: an ad that has SHIPPED shows what went
 * out, and only an ad that has not shipped shows a freshly prepared render. Reversing these two
 * would let a brand change repaint history.
 *
 * ⚠️ `preview_url` is trusted over `shipped_preview_url` only because the save that produced it is
 * newer than the ship. An ad saved after shipping is genuinely different from the one that ran, and
 * the editor is the place where that difference is supposed to be visible.
 */
export const storedPreviewUrl = (
  variant: StoredPreviewVariant | null | undefined
): string | undefined =>
  // ⚠️ An ad with unsaved edits has no stored picture that describes it: every one of them was
  // drawn at the last save. Returning one here froze the rail thumbnail on the previous photo
  // while the canvas showed the new one -- found by Steven on QA, 16 Sep 2026.
  variant?.isDirty
    ? undefined
    : variant?.preview_url ??
      // ⚠️ Not the shipped picture while new pictures are pending. After a changing save the ad no
      // longer looks like what it ran as, and showing that picture also told the editor nothing was
      // missing, so it stopped waiting for the right one (Astra's second opinion, confirmed in
      // AdBuilder's polling condition, 16 Sep 2026).
      // A FAILED drawing after a saved change is the same: the shipped picture is still the ad as
      // it ran (Ihar's review of API PR #4855, point 5). Only `pending` keeps the editor waiting.
      (variant?.preview_pending || variant?.preview_state === 'failed'
        ? undefined
        : variant?.shipped_preview_url) ??
      undefined;

/**
 * SYM-6214. The picture fields a poll may refresh, and nothing else.
 *
 * ⚠️ A poll answer used to REBUILD the editor: every ad replaced, every ad given a new uuid, the
 * canvas and the rail redrawn from nothing. Steven saw it on QA, 16 Sep 2026, as the whole
 * campaign blanking a few times, three seconds apart. Only these fields can have changed because a
 * drawing job finished, so only these are copied across.
 */
export const STORED_PICTURE_FIELDS = [
  'preview_url',
  'template_previews',
  'shipped_preview_url',
  'preview_pending',
  'preview_state',
] as const;

/**
 * SYM-6214. Whether a poll answer describes the SAME ads the editor holds, so its pictures can be
 * copied across instead of rebuilding. A different set of ids means something else changed the
 * campaign (a save added or removed an ad), and only a rebuild handles that correctly.
 */
export const sameSavedAds = (
  held: { id?: number | null; isDeleted?: boolean }[],
  fresh: { id?: number | null }[]
): boolean => {
  const ids = (list: { id?: number | null }[]) =>
    list
      .map((v) => v.id)
      .filter((id): id is number => typeof id === 'number')
      .sort((a, b) => a - b)
      .join(',');

  // An unsaved ad in the editor (no id yet) is not in the answer; a rebuild would throw it away.
  if (held.some((v) => !v.id && !v.isDeleted)) return false;

  return ids(held) === ids(fresh);
};

/**
 * SYM-6214 (Astra's third review, point 2.4). Whether a re-ask's answer for ONE ad describes the
 * save the editor holds, so its pictures may be copied across.
 *
 * The backend refuses to store pictures of a superseded save, but a poll answer built before a
 * save can still arrive after it: the ad on screen is at save N+1 and pending, the answer carries
 * save N's pictures and says ready. Copying it would show the old pictures AND stop the wait for
 * the right ones. Both sides come from the same API, so a missing number on either side (an ad
 * never saved since this shipped) reads as save 0.
 */
export const storedPicturesApply = (
  held: { preview_generation?: number | null },
  fresh: { preview_generation?: number | null }
): boolean =>
  (held.preview_generation ?? 0) === (fresh.preview_generation ?? 0);

/**
 * SYM-6351. A NEWER save that the editor did not make: the server redrew the ad because the
 * campaign's job title or location changed (from the Job title row, or Targeting). Those pictures
 * belong to the ad as it now is, and without taking them the editor showed the old title until a
 * reload -- storedPicturesApply() refuses any number but its own.
 *
 * Only for an ad with no unsaved edits: an edited ad's pictures come from its own save, which draws
 * with the campaign's values anyway. Numbers only ever go up, so a newer number cannot be a stale
 * answer, which is the one thing the guard above exists to stop.
 */
export const storedPicturesAdopt = (
  held: { preview_generation?: number | null; isDirty?: boolean },
  fresh: { preview_generation?: number | null }
): boolean =>
  !held.isDirty &&
  (fresh.preview_generation ?? 0) > (held.preview_generation ?? 0);

export type StoredPreviewVariant = {
  template?: string | null;
  preview_url?: string | null;
  shipped_preview_url?: string | null;
  /** Saved with a change, new pictures not drawn yet: the shipped picture does not describe it. */
  preview_pending?: boolean | null;
  preview_state?: 'pending' | 'ready' | 'failed' | null;
  preview_generation?: number | null;
  template_previews?: Record<string, string> | null;
  /** Unsaved edits: every stored picture predates them, so none of them describes this ad. */
  isDirty?: boolean;
};

/**
 * SYM-6214. Whether the RAIL still has to ask the server about this ad.
 *
 * False once the payload answers, which after this ticket is the normal case: the picture was drawn
 * when the ad was saved. The rail request stays for everything the server has not prepared yet --
 * an ad saved before this shipped, or one whose render is still on the queue.
 *
 * Kept next to storedPreviewUrl() so the two cannot drift: "the rail shows it" and "the rail does
 * not ask for it" must answer from exactly the same fields, or a thumbnail appears AND a request
 * goes out for it.
 */
export const railNeedsRequest = (
  variant: (RailPreviewVariant & StoredPreviewVariant) | null | undefined
): boolean => !!variant?.template && !storedPreviewUrl(variant);

/**
 * SYM-6214. The picker's slots, filled from the pictures the payload already carries.
 *
 * Seeded into templatePreviews when the ad changes, in place of the empty map SYM-5876 left there.
 * Everything downstream then works unchanged: the seeding effect skips a code that HAS a slot, and
 * ensureTemplatePreviews() does the same -- so a fully prepared ad opens the panel without sending
 * anything, and a half-prepared one asks only for what is missing.
 *
 * A code absent from `template_previews` deliberately gets NO slot rather than an empty one: an
 * empty slot would read as "already asked for" and park that tile on a spinner forever, which is
 * the SYM-5919 hang this file has already produced once.
 */
export const tileSlotsFromPayload = (
  variant: StoredPreviewVariant | null | undefined
): PreviewSlots => {
  /*
   * ⚠️ NOTHING is seeded for an ad with unsaved changes, and this is the guard that was missing.
   *
   * The stored pictures were drawn at the LAST SAVE. Seeding them for an ad the customer is still
   * editing shows the previous state as though it were current: Steven swapped an ad's photo on QA
   * on 16 Sep 2026 and the panel kept showing the old one, while the locked designs -- absent from
   * the stored set, so still drawn live -- correctly showed the new photo. The live path already
   * handles an edited ad properly; seeding was overriding it.
   */
  if (variant?.isDirty) return {};

  const prepared = variant?.template_previews;
  const slots = Object.entries(prepared ?? {}).reduce<PreviewSlots>(
    (acc, [code, url]) => {
      if (url) acc[code] = { url };
      return acc;
    },
    {}
  );

  /*
   * ⚠️ The ad's OWN design gets the full-size picture, not its tile. The canvas reads this same
   * slot (StencilCanvas) and shows it at 400x500; the tile is 256px tall, so the big picture came
   * out visibly blurred -- found by Steven on QA, 16 Sep 2026. The panel tile for that one design
   * then shows the full-size picture scaled down, which costs nothing: the canvas loads it anyway.
   */
  if (variant?.template && variant.preview_url) {
    slots[variant.template] = { url: variant.preview_url, full: true };
  }

  return slots;
};

/**
 * SYM-6214. Which of $codes the picker still has to ask the server for.
 *
 * Used by the "preparing" state: while this is non-empty the editor re-asks the ad list a few
 * times, because the pictures are being drawn on the queue and arrive without anyone clicking.
 */
export const missingTileCodes = (
  variant: StoredPreviewVariant | null | undefined,
  codes: string[]
): string[] => {
  const prepared = variant?.template_previews ?? {};
  return codes.filter((code) => !prepared[code]);
};

export type RailPreviewVariant = PreviewInputVariant &
  StoredPreviewVariant & {
    template?: string | null;
  };

/**
 * SYM-6213. What a rail render is remembered under.
 *
 * ⚠️ NOT THE UUID, and that was half of the 2026-09-15 outage. initVariants() hands every ad a
 * fresh uuid on each refetch, so a save made every rail picture look unknown and all of them were
 * asked for again -- the 112-request burst started three seconds after a save.
 *
 * The database id alone is not enough either: an ad edited and saved keeps its id, and keyed on id
 * its thumbnail would never catch up with the edit. So the key is the id plus everything the server
 * draws from, taken from the same list the preview request itself carries. Anything that would
 * change the picture changes the key; a new uuid does not.
 *
 * Undefined for an ad with no id or no template: the rail only asks the server about saved ads.
 */
export const railPreviewKey = (
  variant: RailPreviewVariant | null | undefined
): string | undefined => {
  if (!variant?.id || !variant.template) return undefined;

  return `${variant.id}:${JSON.stringify(
    templatePreviewInput(variant, variant.template, false)
  )}`;
};

/**
 * SYM-5908. Everything the SERVER draws into a template preview, as one key.
 *
 * When any of these changes, every rendered preview is wrong and all of them must be dropped so
 * the seeding effect re-requests them. The list was previously written inline as a React dep
 * array and held two entries, the variant and the photo. The logo was not among them -- and every
 * template draws the logo -- so swapping it left the customer looking at a picture of their old
 * one. Confirmed on QA: a logo swap fired ZERO preview requests and the preview src did not move.
 *
 * Nothing repainted the fabric canvas underneath either, or rather it did, which is the trap: a v2
 * template is a server picture laid OVER that canvas, so changeLogo() dutifully redrew something
 * nobody can see.
 *
 * Lives here as a function rather than inline because the app has no component tests, and this is
 * a rule that gets extended every time a template starts drawing something new. A dep array is the
 * worst possible home for a rule like that: adding to it is invisible, forgetting to is silent,
 * and the symptom is a stale picture rather than an error.
 *
 * One thing it deliberately does NOT include:
 *
 * - The value fields (hook, extra line, salary, benefits, accent). Those are typed, so they are
 *   handled by the debounced refreshTemplatePreview() instead; invalidating everything on every
 *   keystroke would blank the canvas the customer is looking at.
 *
 * ⚠️ `uuid` is a FALLBACK, never a co-key, and the order is the whole point. See below.
 */
export const previewRenderIdentity = (
  variant:
    | {
        id?: number | null;
        uuid?: string | null;
        path?: { id?: number | null } | null;
        logo?: { id?: number | null } | null;
        logo_place?: string | null;
      }
    | null
    | undefined
): string =>
  [
    /*
     * SYM-5919. An ad that has not been saved yet has no id, so every unsaved ad used to produce
     * the SAME identity as every other one. Two of them with the same photo were indistinguishable,
     * and the second showed the first one's pictures: the map is keyed by template code alone, and
     * nothing here said the ad underneath had changed.
     *
     * uuid answers that, but ONLY where there is no id, and the ordering is load-bearing rather
     * than stylistic. initVariants() regenerates uuid on every refetch, so a SAVE hands every
     * variant a new uuid while its id stays the same. Keyed on uuid first, a save would clear all
     * eight previews while the panel, which keys on id, never re-requested them, and the tiles
     * would sit on "Rendering..." forever. That is a bug this file has already had, and the test
     * named "ignores uuid, which a save regenerates" exists to stop it coming back.
     *
     * So: a SAVED ad is identified by its id and is deaf to uuid churn, exactly as before. An
     * UNSAVED one has nothing else to be identified by. The one crossing between them, at the
     * moment of its first save, changes identity once and re-renders once, which is correct: it
     * has just become a different thing, a stored ad the server can look up.
     */
    variant?.id ?? variant?.uuid ?? '',
    variant?.path?.id ?? '',
    variant?.logo?.id ?? '',
    /*
     * SYM-6301. Where the logo sits changes every one of the eight pictures, so a remembered set
     * drawn before the customer moved it is stale.
     *
     * PICKED, not typed, which is the whole reason it is here. Every field this identity and the
     * payload below have ever missed -- the crop, the photo, the logo -- was picked with a click
     * or a drag rather than typed, and each one cost a bug report that read as "my change did not
     * save" while the value had saved perfectly.
     *
     * '' for "not chosen", which is what an untouched ad carries.
     */
    variant?.logo_place ?? '',
  ].join(':');

/**
 * SYM-5990. The colours a preview response should be allowed to write onto the ad.
 *
 * The two pickers display the RESOLVED colour, because an ad with none of its own has to show the
 * brand's rather than a black it never chose (SYM-5929). That value arrives with the variant and is
 * refreshed only by a save, while the picture is redrawn on every change -- so clicking black
 * turned the ad black and left the field reading the old colour until the customer saved. The
 * render now reports the colours it drew with, and this decides whether they are still current.
 *
 * WHY A GUARD AT ALL. The request is debounced by 600ms and the render takes a few hundred more.
 * Pick two colours in quick succession and the FIRST response can land after the second choice, so
 * applying it blindly would drag the field backwards to a colour the ad no longer has -- the same
 * disagreement this ticket is closing, arriving by a different route.
 *
 * The test is the ad's OWN colours, not the resolved ones: they are what the request carried and
 * what the customer manipulates. Equality is case-insensitive because the picker emits lowercase
 * while stored values and brand columns are mixed case, and a case difference is not a change.
 */
export const resolvedColoursFromPreview = (
  sent: { primary?: string | null; accent?: string | null },
  current: { primary_color?: string | null; accent_color?: string | null },
  response:
    | {
        resolved_primary_color?: string | null;
        resolved_accent_color?: string | null;
      }
    | null
    | undefined
): { primary?: string; accent?: string } | null => {
  if (!response) return null;

  const same = (a?: string | null, b?: string | null) =>
    (a ?? '').toLowerCase() === (b ?? '').toLowerCase();

  // The ad moved on while this render was in flight. Its own response is coming.
  if (
    !same(sent.primary, current.primary_color) ||
    !same(sent.accent, current.accent_color)
  ) {
    return null;
  }

  const primary = response.resolved_primary_color ?? undefined;
  const accent = response.resolved_accent_color ?? undefined;

  // An older API, or a deploy where only the app half has landed, sends neither. Writing undefined
  // over a good value would put the pickers back to showing black.
  if (!primary && !accent) return null;

  return { ...(primary ? { primary } : {}), ...(accent ? { accent } : {}) };
};

/**
 * SYM-6013. The body of a template-preview request.
 *
 * ⭐ WHY THIS IS A FUNCTION AND NOT AN OBJECT LITERAL IN THE CONTEXT.
 *
 * This list has now been one field short FOUR times: banner_title (SYM-5961), crop (SYM-5879),
 * path_id (SYM-5908) and logo_id (SYM-6013). Each cost a bug report, and two of them were reported
 * as "my change did not save" because that is exactly what the symptom looks like.
 *
 * The rule they break is simple and worth stating once: the server renders from the STORED variant
 * for everything it is not given, so ANYTHING the customer can change in the editor before saving
 * has to travel with the request.
 *
 * The reason it kept being broken is subtler. Every one of the four is set by CLICKING rather than
 * typing -- a colour swatch, a drag of the crop handles, an upload. Reading down a list of fields
 * looking for "values being edited", none of them registers. So the rule alone has not been enough,
 * and this is here so a test can hold the list to it instead.
 *
 * Extracted for the same reason previewsAfterValueChange() and previewRenderIdentity() were: this
 * app has no component tests, so a decision left inside the context cannot be exercised at all.
 */
export type PreviewInputVariant = {
  id?: number | null;
  placement?: string | null;
  creative_type?: string | null;
  function?: string | null;
  accent_color?: string | null;
  primary_color?: string | null;
  banner_title?: string | null;
  cta?: string | null;
  crop?: unknown;
  path?: { id?: number | null } | null;
  logo?: { id?: number | null } | null;
  /** SYM-6301. Where the customer put the logo, or null for "not chosen". */
  logo_place?: string | null;
  free_text?: string | null;
  subtext?: string | null;
  salary?: string | null;
  benefits?: unknown;
};

/**
 * Every value the customer can change before saving, plus the template being drawn.
 *
 * `isUnsaved` adds the three the server cannot look up for an ad that has no row yet. They are
 * REQUIRED there -- they are what the guards in VisualService judge -- and omitted for a stored ad,
 * where the row is the authority on them.
 */
export const templatePreviewInput = (
  variant: PreviewInputVariant,
  code: string,
  isUnsaved: boolean
): Record<string, unknown> => ({
  ...(isUnsaved
    ? {
        placement: variant.placement,
        creative_type: variant.creative_type,
        function: variant.function ?? null,
      }
    : {}),
  template: code,
  accent_color: variant.accent_color ?? null,
  // The badge. Reported by Mark and Steven as the callout updating in the field and not in the
  // picture until a manual refresh; the value was saving correctly all along.
  banner_title: variant.banner_title ?? null,
  // The "Brand colour" picker in the same panel.
  primary_color: variant.primary_color ?? null,
  // Drawn into the image by J1 only. NOTE: the CTA dropdown does not call refreshTemplatePreview()
  // at all, so J1's button still will not re-render on change. Separate gap.
  cta: variant.cta ?? null,
  // Dragged, not typed -- which is why it was missed under SYM-5879.
  crop: variant.crop ?? null,
  // Uploaded, not typed. Swap a picture and, without this, the cropper shows the new one while the
  // ad keeps the old one.
  path_id: variant.path?.id ?? null,
  // Uploaded, not typed. Without this a logo picked on a STORED ad never reached the render at all:
  // the previews were correctly discarded and re-requested, and came back freshly drawn from the
  // stored row -- so they were new pictures of the old logo, and Save did not shift them either.
  logo_id: variant.logo?.id ?? null,
  // SYM-6301. Picked, not typed. Without it the four buttons move nothing until Save: the server
  // draws from the STORED value for anything it is not given, so the preview would come back
  // showing the logo where it used to be.
  logo_place: variant.logo_place ?? null,
  free_text: variant.free_text ?? null,
  subtext: variant.subtext ?? null,
  salary: variant.salary ?? null,
  benefits: variant.benefits ?? null,
});

/**
 * SYM-6023. Whether a thumbnail captured from the canvas may be written to the ad it was meant for.
 *
 * ⚠️ THE THUMBNAIL IS PHOTOGRAPHED LATE. generateDataUrl() waits for every image on the canvas to
 * finish decoding before it calls toDataURL(), and only THEN writes the result. The ad's identity
 * is captured up front -- someone already fixed that half -- but the CANVAS CONTENTS are read at
 * the end, and nothing checked the canvas still belonged to that ad.
 *
 * So: the app starts capturing ad 3, the customer clicks ad 1 before the images finish loading, the
 * canvas swaps to ad 1, the photograph is finally taken, and ad 1's picture is filed as ad 3's
 * thumbnail. Steven watched exactly that on QA on 2026-08-19: ad 3 was showing a footballer under a
 * v2 template, he clicked ad 1, and ad 3's thumbnail became ad 1's railway photo with an old square
 * overlay on it. He then saved, which persisted it.
 *
 * ⭐ THE GUARD PROTECTED THE DESTINATION AND NOT THE SOURCE, which is why capturing the uuid looked
 * like it had solved this. It only stops the write landing on whatever happens to be selected; it
 * cannot notice that the picture being written is of a different ad entirely.
 *
 * Returns false when the canvas has moved on, and the capture is simply dropped -- the ad keeps the
 * thumbnail it had, which is stale at worst. Writing a picture of the wrong ad is not.
 */
export const canWriteCapturedThumbnail = (
  capturedFor: string | null | undefined,
  canvasNowShows: string | null | undefined
): boolean => Boolean(capturedFor) && capturedFor === canvasNowShows;

/**
 * SYM-6035. Can this ad be previewed at all yet, or is it still waiting for a photograph?
 *
 * Every template that draws a picture is refused with a 400 carrying "Template x requires a
 * photo.", and a 400 is not quiet: NotificationContext turns every non-2xx into a toast. So an ad
 * added with the + button produced one red toast per template -- seven on feed, eight on story --
 * before the customer had done anything at all.
 *
 * Split out here rather than left inline because the seeding effect it guards cannot be exercised
 * in a test, and a rule nobody can run is a rule nobody can change safely.
 *
 * ⚠️ Answers for ALL templates, including B2, which needs no photo and would have rendered. The
 * frontend has no way to ask which templates need one (SYM-6024), and hard-coding that answer here
 * would be a second copy of a fact the server owns -- the mistake this epic has already made four
 * times. Once the API declares it, this becomes a per-template question.
 */
export const variantAwaitsPhotoForPreview = (
  variant: { path?: { id?: number | null } | null } | null | undefined
): boolean => !variant?.path?.id;

/** What a tile says while its ad has no photo to draw with (SYM-6035). */
export const PHOTO_NEEDED_NOTICE = 'Add a photo to preview this template.';

/**
 * The preview map with "add a photo" written on every listed design that has no entry yet.
 *
 * ⚠️ Hands back THE SAME MAP when nothing was written. The seeding effect that calls this lists
 * the map among what it reacts to, so a fresh copy every time made that effect run again at once,
 * for as long as an ad without a photo was selected: about 360 redraws of the ad builder a second,
 * measured on 9 Oct 2026 (2159 in six seconds, React warning "Maximum update depth exceeded").
 * It showed as nothing worse than a busy processor until something on the screen needed two quiet
 * seconds.
 *
 * Split out for the reason variantAwaitsPhotoForPreview is: the effect cannot be run in a test.
 */
export const withPhotoNeededNotices = <Slot extends { error?: string }>(
  previews: Record<string, Slot>,
  codes: string[]
): Record<string, Slot> => {
  const missing = codes.filter((code) => !previews[code]);
  if (!missing.length) return previews;

  const next = { ...previews };
  missing.forEach((code) => {
    next[code] = { error: PHOTO_NEEDED_NOTICE } as Slot;
  });

  return next;
};

/**
 * SYM-6214 (Astra's check D on QA, 17 Sep 2026). When the signed picture links in a payload stop
 * working, as a time in milliseconds, or undefined when the link is not a signed one.
 *
 * Every stored picture is handed out as a signed link that lives 60 minutes. The editor kept
 * those links for as long as it stayed open: after the hour, opening the designs picker asked the
 * storage for nine tiles with dead links and every one came back "Request has expired". Nothing
 * asked the API for fresh ones, so the tiles stayed broken. The signing scheme puts the minting
 * time (X-Amz-Date, 20260917T062051Z) and the lifetime in seconds (X-Amz-Expires) in the link
 * itself, so the editor can know when to ask again without the storage having to tell it.
 */
export const storedPictureExpiry = (
  url: string | null | undefined
): number | undefined => {
  if (!url) return undefined;
  const date = /[?&]X-Amz-Date=(\d{8}T\d{6}Z)/.exec(url);
  const expires = /[?&]X-Amz-Expires=(\d+)/.exec(url);
  if (!date || !expires) return undefined;
  const d = date[1];
  const minted = Date.UTC(
    Number(d.slice(0, 4)),
    Number(d.slice(4, 6)) - 1,
    Number(d.slice(6, 8)),
    Number(d.slice(9, 11)),
    Number(d.slice(11, 13)),
    Number(d.slice(13, 15))
  );
  return minted + Number(expires[1]) * 1000;
};

/**
 * The earliest expiry among the stored picture links these ads carry, or undefined when none is
 * signed. One number for the whole campaign: the links are minted together by one payload, so they
 * expire together, and one refresh renews them all.
 */
export const earliestStoredPictureExpiry = (
  variants: StoredPreviewVariant[]
): number | undefined => {
  let earliest: number | undefined;
  variants.forEach((v) => {
    const urls = [
      v.preview_url,
      v.shipped_preview_url,
      ...Object.values(v.template_previews ?? {}),
    ];
    urls.forEach((u) => {
      const t = storedPictureExpiry(u);
      if (t !== undefined && (earliest === undefined || t < earliest))
        earliest = t;
    });
  });
  return earliest;
};

/**
 * SYM-6214 (Astra's fifth review). When the editor next asks for fresh picture links, given when
 * the current ones die, when it last asked, and now. Pure, so the schedule can be tested at the
 * production lifetime with a controlled clock instead of a live hour.
 *
 * The latest of three moments: expiry minus the margin (renew shortly before the links die), one
 * minute after the previous attempt (never a tight loop, whether that attempt succeeded or failed),
 * and one second from now (never in the past). A renewal brings new links whose expiry re-arms this.
 */
export const RENEWAL_MARGIN_MS = 2 * 60_000;
export const RENEWAL_MIN_GAP_MS = 60_000;

export const nextLinkRenewalAt = (
  expiresAt: number,
  lastAttemptAt: number,
  now: number
): number =>
  Math.max(
    expiresAt - RENEWAL_MARGIN_MS,
    lastAttemptAt + RENEWAL_MIN_GAP_MS,
    now + 1_000
  );
